import React, { useState } from "react";
import { PlayCircle } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { youtubeEmbedUrl, youtubeThumbnailUrl } from "@/lib/youtube";

/**
 * TrailerEmbed — click-to-play YouTube trailer.
 *
 * Accepts any YouTube URL flavour (watch, youtu.be, embed, shorts) and:
 *   1. Renders a thumbnail (uploaded → fallback to auto-generated hqdefault).
 *   2. Loads the iframe only after the user clicks Play (privacy + perf).
 *   3. Falls back to the existing editable placeholder if the URL is missing
 *      or not a recognised YouTube URL.
 */
export const TrailerEmbed = ({ url, thumbnail, title = "Book trailer" }) => {
  const [playing, setPlaying] = useState(false);
  const embed = youtubeEmbedUrl(url);
  const autoThumb = youtubeThumbnailUrl(url);
  const thumbSrc = thumbnail ? mediaUrl(thumbnail) : autoThumb;

  if (!embed) {
    // Hide the trailer block entirely when no valid URL is set.
    return null;
  }

  if (playing) {
    return (
      <div className="aspect-video border" style={{ borderColor: "var(--gi-border)" }} data-testid="book-trailer-embed">
        <iframe
          src={`${embed}?autoplay=1&rel=0`}
          title={title}
          className="w-full h-full"
          allow="accelerated-encoding; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className="relative aspect-video border w-full group overflow-hidden"
      style={{ borderColor: "var(--gi-border)", background: "#000" }}
      data-testid="book-trailer-thumb"
      aria-label={`Play ${title}`}
    >
      {thumbSrc && (
        <img
          src={thumbSrc}
          alt={`${title} thumbnail`}
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
          loading="lazy"
        />
      )}
      <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.28)" }}>
        <div className="w-16 h-16 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: "var(--gi-maroon)", color: "var(--gi-ivory)" }}>
          <PlayCircle size={36} />
        </div>
      </div>
    </button>
  );
};

export default TrailerEmbed;
